/**
 * UNA SOLA FORMA DE ARMAR LA FILA DE UNA SOLICITUD DE DOCUMENTOS.
 *
 * Lissette, 2026-09-04, queja 76: *"en seguimiento de documentos se tiene que
 * refrescar para que salgan los nombres, y luego te sale y entras y no estan;
 * se tiene que estar refrescando para que salga anotado tambien en la lista"*.
 *
 * ── POR QUE PASABA ──────────────────────────────────────────────────────────
 *
 * La misma fila se armaba a mano en TRES sitios:
 *
 *   1. al cargar la pantalla            (deskte-full.jsx, hidratacion)
 *   2. al crear la solicitud            (DocRequests.jsx)
 *   3. al subir el cliente un archivo   (deskte-full.jsx, aviso en vivo)
 *
 * El 2026-08-20 se arreglo el (1) —le faltaban `clientName` y `title`, y por eso
 * todas las filas salian con "—" en Cliente—. Los otros dos se quedaron como
 * estaban. Resultado, exactamente lo que ella describe:
 *
 *   - recien creada, la fila sale SIN nombre (la armo el sitio 2);
 *   - al refrescar la pagina sale bien (la arma el sitio 1);
 *   - y en cuanto el cliente sube un archivo, el aviso en vivo vuelve a
 *     REEMPLAZAR la lista entera con filas sin nombre (el sitio 3) — el nombre
 *     "estaba y desaparece" sin que nadie toque nada.
 *
 * Es la regla de oro 118 otra vez: cuando algo se hace en tres sitios, arreglar
 * uno no es arreglarlo. Y el E-138: dos copias del mismo mapeo acaban diciendo
 * cosas distintas.
 *
 * ── AHORA ───────────────────────────────────────────────────────────────────
 *
 * Los tres caminos pasan por aqui. Si manana hace falta un campo mas en la
 * fila, se anade una vez y lo heredan los tres.
 *
 * Modulo sin React a proposito: se prueba con datos inventados.
 */

/**
 * El estado de CADA documento de la fila se deduce del estado de la solicitud.
 * `documentsNeeded` guarda la lista entera aunque solo falte uno, asi que no se
 * puede saber cual llego: parcial y pendiente se quedan las dos en "pendiente".
 */
export function estadoDelDocumento(estadoDeLaSolicitud) {
  if (estadoDeLaSolicitud === 'completed') return 'received';
  if (estadoDeLaSolicitud === 'expired') return 'overdue';
  return 'pending';
}

/**
 * El DIA LOCAL de una marca de tiempo del servidor.
 *
 * Cortar por la "T" del ISO da el dia en UTC, y en Florida (UTC-4) todo lo que
 * se manda despues de las 8 de la noche saldria fechado MANANA. Es la regla de
 * la casa: el dia local, nunca el de `toISOString()`.
 */
export function diaLocal(iso) {
  const t = String(iso || '').trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;   // ya viene sin hora
  const d = new Date(t);
  if (isNaN(d.getTime())) return t.split('T')[0];
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * La fila que pinta Seguimiento / Envios, a partir de la solicitud que
 * devuelve el servidor (ya pasada por `mapDocRequestFromApi` de api.js).
 *
 * @param {object} r        La solicitud del servidor.
 * @param {object} [opts]
 * @param {string} [opts.origen]  Para armar el enlace cuando el servidor no lo
 *                                manda (window.location.origin).
 * @param {Array}  [opts.docs]    Los documentos TAL COMO se pidieron en el
 *                                formulario, con su marca de obligatorio — que
 *                                el servidor no devuelve. Solo al crear.
 */
export function filaDeSolicitud(r, opts) {
  const s = r || {};
  const o = opts || {};
  const necesarios = Array.isArray(s.documentsNeeded) ? s.documentsNeeded : [];
  const estado = s.status || 'pending';
  const token = s.portalToken || '';
  return {
    id: s.id,
    clientId: s.clientId || '',
    // Los dos que se perdian, y por los que la fila salia sin nombre.
    clientName: s.clientName || '',
    title: s.title || '',
    documentsNeeded: necesarios,
    docs: Array.isArray(o.docs) && o.docs.length
      ? o.docs
      : necesarios.map((name) => ({ name, required: false, status: estadoDelDocumento(estado) })),
    sentAt: diaLocal(s.sentAt) || diaLocal(s.createdAt),
    uploadToken: token,
    link: s.portalUrl || (o.origen ? `${o.origen}/upload/${token}` : ''),
    email: s.clientEmail || '',
    status: estado,
  };
}
